'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'

// lib
import { ThemeVariantContext } from '../../_support'
import { accessibilityRole, distribute } from '../../_support'
import Cover, { CoverProps, ThemedCover } from '../../cover'
import Text, { TextProps } from '../../text'
import { FieldState } from './use-field'
import { colors } from '@theme'

type ColorsKeys = keyof typeof colors

/* -----------------------------------------------------------------------------
 * FieldLabel
 * -------------------------------------------------------------------------- */

export type FieldLabelProps = CoverProps &
  TextProps & {
    children: React.ReactNode
    fieldState: FieldState
    backgroundColor?: ColorsKeys | string
    error?: boolean
  }

const FieldLabel = ({
  children,
  error,
  fieldState,
  backgroundColor = 'transparent',
  ...rest
}: FieldLabelProps) => {
  const themeVariant = React.useContext(ThemeVariantContext)
  const [coverProps, textProps] = distribute(rest, {
    using: ThemedCover.styleKeys,
    to: {},
    rest: {}
  })

  const coverStyles = {
    default: {
      zIndex: 1,
      padding: '12',
      pointerEvents: 'none'
    },
    empty: {
      justifyContent: 'center'
    },
    focused: {
      pt: '12'
    },
    filled: {
      pt: '12'
    }
  } as const

  const textStyles = {
    empty: {
      default: {
        font: 'text.15.medium',
        textColor: 'shade05'
      },
      punchThroughGray: {
        font: 'text.15.medium',
        textColor: 'shade06'
      }
    },
    focused: {
      default: {
        variant: 'meta',
        textColor: 'violet'
      },
      punchThroughGray: {
        variant: 'meta',
        textColor: 'shade06'
      }
    },
    filled: {
      default: {
        variant: 'meta',
        textColor: 'purple'
      },
      punchThroughGray: {
        variant: 'meta',
        textColor: 'shade06'
      }
    },
    error: {
      textColor: 'error'
    }
  } as const

  const coverStyleProps = {
    ...coverStyles.default,
    ...coverStyles[fieldState]
  }

  const textStyleProps = {
    ...textStyles[fieldState][themeVariant || 'default'],
    ...(error && fieldState === 'filled' ? textStyles.error : undefined)
  }

  return (
    <Cover {...coverStyleProps} {...coverProps}>
      <Text
        accessibilityRole={accessibilityRole('label')}
        {...textStyleProps}
        {...textProps}
        backgroundColor={backgroundColor}
      >
        {children}
      </Text>
    </Cover>
  )
}

export default FieldLabel
